/**
 * Simple in-memory cache implementation.
 *
 * @param T The type of items stored in the cache.
 */
export default class Cache<T> {
  private readonly items: { [key: string]: T } = {};
  private readonly itemLoader: (key: string) => Promise<T>;

  /**
   * Constructs a Cache instance.
   *
   * @param {CacheConstructorArgs} args The constructor arguments.
   */
  constructor(args: CacheConstructorArgs<T>) {
    this.itemLoader = args.itemLoader;
  }

  /**
   * Returns whether the cache contains a specific key.
   *
   * @param {string} key The key to check in the cache.
   * @return {boolean} True if the cache contains the key, false otherwise.
   * @private
   */
  private containsKey(key: string): boolean {
    return Object.keys(this.items).indexOf(key) !== -1;
  }

  /**
   * Gets an item from the cache. If the item is not present, it uses the cacheItemLoader to load it.
   *
   * @param {string} key The key of the item to retrieve.
   * @return The cached or loaded item.
   */
  public get(key: string): Promise<T | null> {
    if (this.containsKey(key)) {
      return Promise.resolve(this.items[key]);
    }

    return this
        .itemLoader(key)
        .then(item => {
          this.items[key] = item;
          return item;
        });
  }
}

/**
 * Arguments for constructing a Cache instance.
 *
 * @param T The type of items stored in the cache.
 */
export interface CacheConstructorArgs<T> {
  itemLoader: (key: string) => Promise<T>;
}
